import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { css } from "@emotion/react";
import type { HTMLAttributes, ReactNode } from "react";

type Skin = "info" | "grey" | "success";

export type ChipV2Size = "small" | "large";
export const ChipV2 = (
  props: {
    label: ReactNode;
    onDelete?: () => void;
    disabled?: boolean;
    hoverable?: boolean;
    size?: ChipV2Size;
    skin?: Skin;
  } & HTMLAttributes<HTMLDivElement>,
) => {
  const {
    label,
    onDelete,
    onClick,
    disabled = false,
    hoverable = true,
    skin = "info",
    size = "large",
    ...restProps
  } = props;
  return (
    <div
      css={chipContainer({
        disabled,
        clickable: !!onClick && !disabled,
        hoverable,
        skin,
      })}
      onClick={onClick}
      {...restProps}
    >
      <div
        css={labelContainer({
          size: size,
        })}
      >
        {label}
      </div>

      {!!onDelete && (
        <i
          onClick={(e) => {
            e.stopPropagation();
            if (disabled) {
              return;
            }
            onDelete?.();
          }}
          className={"icon-15-clear_x"}
          css={deleteIcon({
            disabled,
          })}
        />
      )}
    </div>
  );
};

const deleteIcon =
  ({ disabled }: { disabled: boolean }) =>
  (theme: ExactTheme) => css`
    color: ${theme.colorsV2[disabled ? "grey" : "blue"][500]};
    font-size: 15px;
    display: flex;
    align-items: center;
    padding: 5px;
    margin-right: -5px;
    :hover {
      color: ${theme.colorsV2[disabled ? "grey" : "red"][500]};
    }
  `;

const chipContainer =
  ({
    disabled,
    skin,
    clickable,
    hoverable = true,
  }: {
    disabled: boolean;
    skin: Skin;
    clickable: boolean;
    hoverable: boolean;
  }) =>
  (theme: ExactTheme) => {
    const getBorderColor = () => {
      if (disabled) {
        return theme.colorsV2["grey"][500];
      }
      if (skin === "info") {
        return theme.colorsV2["blue"][500];
      }
      if (skin === "success") {
        return theme.colorsV2["green"][500];
      }
      return theme.colorsV2["grey"][200];
    };

    return css`
      height: 25px;
      cursor: ${disabled ? "not-allowed" : clickable ? "pointer" : "default"};
      padding: 0 10px;
      border-radius: 15px;
      border: 1px solid ${getBorderColor()};
      display: inline-flex;
      align-items: center;

      min-width: 0;
      :hover {
        background-color: ${disabled || !clickable || !hoverable
          ? "transparent"
          : theme.colorsV2.grey[50]};
      }
    `;
  };

const labelContainer =
  ({ size }: { size: ChipV2Size }) =>
  (theme: ExactTheme) => css`
    white-space: nowrap;
    font-size: ${{
      small: "13px",
      large: "16px",
    }[size]};
    overflow: hidden;
    text-overflow: ellipsis;
  `;
